import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslocoPipe, provideTranslocoScope } from '@ngneat/transloco';
import { CompanyImpactProfileService } from 'app/core/organization/company/company-profile/company-profile.service';
import { OrganizationService } from 'app/core/organization/organization.service';
import {
    Corporation,
    isCorporation,
} from 'app/core/organization/organization.types';
import {
    Observable,
    Subject,
    filter,
    map,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs';

@Component({
    selector: 'app-company-profile-actions',
    standalone: true,
    templateUrl: './company-profile-actions.component.html',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatIconModule,
        TranslocoPipe,
    ],
    providers: [
        provideTranslocoScope('corpo/impact-profile'),
        provideTranslocoScope('corpo/company-profile-settings'),
    ],
})
export class CompanyProfileActionsComponent implements OnInit, OnDestroy {
    public company$!: Observable<Corporation | null>;
    public shouldDisplayProfileActions$!: Observable<boolean>;

    public publishedControl = new FormControl(false);

    private _unsubscribeAll = new Subject<null>();

    constructor(
        private readonly _organizationService: OrganizationService,
        private readonly _companyImpactProfileService: CompanyImpactProfileService,
        private readonly _clipboard: Clipboard
    ) { }

    ngOnInit(): void {
        this.company$ = this._organizationService.organization$.pipe(
            map((orga) => {
                if (orga && isCorporation(orga)) {
                    return orga;
                }
                return null;
            })
        );
        this.shouldDisplayProfileActions$ =
            this._companyImpactProfileService.shouldDisplayProfileActions$;

        this.company$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((company) => !!company),
                tap((company) => {
                    this.publishedControl.patchValue(
                        company!.isProfilePublished === true,
                        { emitEvent: false }
                    );
                })
            )
            .subscribe();

        this.publishedControl.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                switchMap(() => this.company$.pipe(take(1))),
                filter((company) => !!company),
                switchMap((company) =>
                    this._organizationService.update({
                        _id: company!._id,
                        type: company!.type,
                        isProfilePublished:
                            this.publishedControl.value === true,
                    })
                )
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    public openProfile(company: Corporation | null): void {
        if (company && company._id) {
            window.open(
                `${window.location.protocol}//${window.location.host}/corporation/${company._id}`
            );
        }
    }

    public copyLink(): void {
        this._organizationService.organization$
            .pipe(take(1))
            .subscribe((orga) => {
                this._clipboard.copy(
                    `${window.location.origin}/corporation/${orga?._id}`
                );
            });
    }
}
